import type { ReactNode } from 'react';
import { lazy } from 'react';

import { array, constant, constNull, option, pipe, struct } from '@code-expert/prelude';
import type { TaskId } from '/imports/domain';
import { RouterButton } from '/imports/ui/components/button/RouterButton';
import { Seal } from '/imports/ui/components/Seal';
import { DateTime } from '/imports/ui/foundation/DateTime/components';
import { VStack } from '/imports/ui/foundation/Layout';
import { styled } from '/imports/ui/foundation/Theme';
import type { QuizAttemptId } from '../../domain';
import type { PublicQuestion } from '../../domain/PublicQuestion';
import type { PublicQuiz } from '../../domain/PublicQuiz';
import { foldPublicQuizByState } from '../../domain/PublicQuiz';
import { solveQuestionLink } from './routes';

const SolveQuizIntro = lazy(async () => {
  const { SolveQuizIntro } = await import('./SolveQuizIntro');
  return { default: SolveQuizIntro };
});

interface Props {
  quiz: PublicQuiz;
  attemptId: QuizAttemptId;
}

export const SolveQuizLandingPage = ({ quiz, attemptId }: Props) => (
  <VStack gap="lg" align="start">
    {foldPublicQuizByState<PublicQuiz, ReactNode>(quiz, {
      editable: constNull,
      submitted: (x) => <SubmittedSeal date={x.submissionDate} />,
      feedback: (x) => <SubmittedSeal date={x.submissionDate} />,
    })}
    <SolveQuizIntro markdown={quiz.description} isExpanded />
    <StartButton
      taskId={quiz.taskId}
      attemptId={attemptId}
      questions={quiz.questions}
      label={foldPublicQuizByState(quiz, {
        editable: constant('Start quiz'),
        submitted: constant('View quiz submission'),
        feedback: constant('View quiz solution'),
      })}
    />
  </VStack>
);

const SubmittedSeal = ({ date }: { date: Date }) => (
  <Seal tilted patchy>
    <VStack align="center">
      <SubmittedText>Submitted</SubmittedText>
      <DateTime date={date} format="date" />
    </VStack>
  </Seal>
);

const SubmittedText = styled('span', ({ tokens }) => ({
  textTransform: 'uppercase',
  fontSize: tokens.fontSizeXL,
}));

export const StartButton = ({
  taskId,
  attemptId,
  questions,
  label,
}: {
  taskId: TaskId;
  attemptId: QuizAttemptId;
  questions: Array<PublicQuestion>;
  label: string;
}) =>
  pipe(
    questions,
    array.head,
    option.map(struct.get('questionId')),
    option.fold(constNull, (questionId) => (
      <RouterButton to={solveQuestionLink({ taskId, attemptId, questionId })} type="primary">
        {label}
      </RouterButton>
    )),
  );
